/**
 * Decoration System Utilities
 *
 * Provides type-safe access to active decoration sets via environment variables.
 * Decoration sets are enabled explicitly via NEXT_PUBLIC_ACTIVE_DECORATIONS.
 */

/**
 * Valid decoration set names.
 * Add new decoration sets here as they are created.
 */
export const VALID_DECORATION_SETS = [
  "halloween",
  "fall",
  "winter",
  "valentines",
  "summer",
] as const;

/**
 * Type representing a valid decoration set name.
 */
export type DecorationSetName = (typeof VALID_DECORATION_SETS)[number];

/**
 * Type guard to check if a string is a valid decoration set name.
 *
 * @param name - The string to check
 * @returns True if name is a valid decoration set name
 */
export function isDecorationSetName(name: string): name is DecorationSetName {
  return VALID_DECORATION_SETS.includes(name as DecorationSetName);
}

/**
 * Gets the active decoration set from environment variables.
 *
 * @returns The active decoration set name, or null if no decorations are enabled
 * @example
 * ```ts
 * const activeSet = getActiveDecorationSet(); // "halloween" | null
 * ```
 */
export function getActiveDecorationSet(): DecorationSetName | null {
  const envValue = process.env.NEXT_PUBLIC_ACTIVE_DECORATIONS;

  // If not set or empty, no decorations
  if (!envValue || envValue.trim() === "") {
    return null;
  }

  const setName = envValue.trim().toLowerCase();

  // Validate against valid decoration sets using type guard
  if (!isDecorationSetName(setName)) {
    // Warn in development mode about invalid decoration set names
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[Decorations] Invalid decoration set name: "${envValue}". Valid options are: ${VALID_DECORATION_SETS.join(", ")}. Decorations will be disabled.`
      );
    }
    return null;
  }

  return setName;
}
